import { Box, Chip, CircularProgress, Grid } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import moment from "moment";
import OrdersNavigation from 'Navigation/OrdersNavigation';
import { useState } from 'react';
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { useAsync } from "react-use";
import AppointmentInformation from "../../../Components/AppointmentInformation";
import Layout from "../../../Components/Layout/Layout";
import Tile from "../../../Components/Tiles/Tile";
import TileContent from "../../../Components/Tiles/TileContent";
import VinDisplay from "../../../Components/VinDisplay";
import dateFormats from "../../../system/dateFormats";
import { AssessmentOrder, UserRole, VehicleAssessment } from "../../../system/Domain";
import useAsyncEffect from "../../../system/useAsyncEffect";
import AssessmentOverviewTile from "../../Vehicles/Assessments/AssessmentOverviewTile";
import TextGroup from "../../VehicleSales/TextGroup";
import TextLine from "../../VehicleSales/TextLine";
import OrdersSubMenu from "../OrdersSubMenu";
import AssessmentOrderStatusPill from "./AssessmentOrderStatusPill";
import DekraAssessmentOrderTile from "./DekraAssessmentOrderTile";
import ExpertAssesmentTile, { AssesmentInterruptionFormType } from "./ExpertAssesmentTile";
import useUser from "../../../system/useUser";
import RepairOrderDisplay from "./RepairOrderDisplay";
import AssessmentOrderDetailsSidebar from "./AssessmentOrderDetailsSidebar";
import InquiryAssesmentTile from "./InquiryAssessmentTile";
import MacadamAssessmentOrderTile from "./MacadamAssessmentOrderTile";
import CompoundPill from "../../../Components/CompoundPill";
import useCompounds from "../../../system/useCompounds";
import useVehicle from "../../../system/useVehicle";
import VehicleAccidentsTile from "../../Vehicles/VehicleAccidentsTile";

const AssessmentOrderDetails = () => {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const [assessmentOrder, setAssessmentOrder] = useState<AssessmentOrder>();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [currentAssessment, setCurrentAssessment] = useState<VehicleAssessment | null>(null);
	const [isEditingDefect, setIsEditingDefect] = useState<boolean>(false);
	const [isEditingInquiryRequest, setIsEditingInquiryRequest] = useState<boolean>(false);
	const [, , hasRole] = useUser();
	const [compounds] = useCompounds({ includeAmazonCompounds: false });
	const [vehicle, setVehicle] = useVehicle(assessmentOrder?.vehicle?.id);

	const isAldManager = hasRole(UserRole.AldManager);
	const isAldUser = hasRole(UserRole.AldUser);

	useAsync(async () => {
		if (!assessmentOrder?.assessmentId) {
			setCurrentAssessment(null);
			return;
		}

		const result = await axios.get<VehicleAssessment[]>(`/api/assessments?id=${assessmentOrder.assessmentId}`);
		setCurrentAssessment(result.data[0] ?? null);
	}, [assessmentOrder]);

	useAsyncEffect(async () => {
		if (!id) {
			return;
		}

		const { data: ao } = await axios.get<AssessmentOrder[]>(`/api/orders/assessments`, {
			params: {
				id: id
			}
		});

		if (ao.length !== 1) {
			history.push('/orders/assessments');
			return;
		}
		setAssessmentOrder(ao[0]);
	}, [id]);

	const saveAssessmentInterruption = async (formData: AssesmentInterruptionFormType): Promise<AssessmentOrder> => {
		let responseData: AssessmentOrder;
		let response: AxiosResponse<AssessmentOrder>;
		if (!formData) {
			return;
		}

		setIsSubmitting(true);

		try {
			if (assessmentOrder.defect) {
				response = await axios.put<AssessmentOrder>(`/api/orders/assessments/${assessmentOrder.id}/disruption`, formData);
			} else {
				response = await axios.post<AssessmentOrder>(`/api/orders/assessments/${assessmentOrder.id}/disruption`, formData);
			}
			responseData = response.data;
			setAssessmentOrder(response.data);
		} finally {
			setIsSubmitting(false);
		}

		return responseData;
	};

	const updateInterruptionFormState = (defectForm: boolean, inquiryForm: boolean) => {
		setIsEditingDefect(defectForm);
		setIsEditingInquiryRequest(inquiryForm);
	};

	const hasAnySubjectInqueries = assessmentOrder?.subjectInquiries !== null && assessmentOrder?.subjectInquiries.length > 0;

	return (
		<Layout
			title="Gutachtenauftrag"
			plateNumber={assessmentOrder?.plateNumber}
			navigation={<OrdersNavigation
				vehicle={isAldUser && assessmentOrder ? {
					id: assessmentOrder.vehicle.id,
					leasmanContractId: assessmentOrder.leasmanContract?.id,
					plateNumber: assessmentOrder.plateNumber
				} : undefined}
			/>}
			subMenu={<OrdersSubMenu />}
			sidebar={
				<AssessmentOrderDetailsSidebar
					assessmentOrder={assessmentOrder}
					setAssessmentOrder={setAssessmentOrder}
					isEditingDefect={isEditingDefect}
					isEditingInquiryRequest={isEditingInquiryRequest}
					startDefect={() => updateInterruptionFormState(true, false)}
					startInquiryRequest={() => updateInterruptionFormState(false, !isEditingInquiryRequest)}
					canNavigateToAssessments={currentAssessment !== null}
					vehicle={vehicle}
					setVehicle={setVehicle}
				/>
			}
		>
			{!assessmentOrder && <CircularProgress size={48} />}
			{assessmentOrder && (
				<Box>
					<Grid container spacing={6}>
						<Grid item xs={12} md={6}>
							<Tile title="Auftrag">
								<TileContent>
									<TextGroup>
										<TextLine label="Typ" value={
											<Chip
												size="small"
												label={assessmentOrder.assessmentType}
											/>
										} />
									</TextGroup>
									{assessmentOrder.compound && compounds && (
										<TextGroup>
											<TextLine label="Compound" value={<CompoundPill compounds={compounds} compoundRef={assessmentOrder.compound} />} />
										</TextGroup>
									)}
									<TextGroup>
										<TextLine
											label="Adresse"
											bold
											value={assessmentOrder.address.name}
										/>
										<TextLine
											label=""
											value={`${assessmentOrder.address.street} - ${assessmentOrder.address.zipCode}`}
										/>
										<TextLine
											label=""
											value={`${assessmentOrder.address.city} - ${assessmentOrder.address.country}`}
										/>
										<TextLine
											label="Zusatzinformationen"
											value={assessmentOrder.address.additionalInformation}
										/>
									</TextGroup>
									<TextGroup>
										<TextLine
											label="Kontaktperson"
											value={`${assessmentOrder.contact?.firstName} ${assessmentOrder.contact?.lastName}`}
										/>
										<TextLine
											label=""
											value={`${assessmentOrder.contact?.email} - ${assessmentOrder.contact?.telephone}`}
										/>
									</TextGroup>
									<TextGroup>
										<TextLine
											label="Termin"
											value={
												<AppointmentInformation appointment={assessmentOrder.appointment} noMargin />
											}
										/>
									</TextGroup>
									{assessmentOrder.comment && (
										<TextGroup>
											<TextLine label="Kommentar" value={assessmentOrder.comment} />
										</TextGroup>
									)}
								</TileContent>
							</Tile>
						</Grid>

						<Grid item xs={12} md={6}>
							<Tile title="Auftragsdetails">
								<TileContent>
									<TextGroup>
										<TextLine
											label="Status"
											value={
												<AssessmentOrderStatusPill
													assessmentOrder={assessmentOrder}
													additionalInformation={
														assessmentOrder.cancellationReason ? (
															<>{assessmentOrder.cancellationReason}</>
														) : undefined
													}
												/>
											}
										/>
									</TextGroup>
									<TextGroup>
										<TextLine
											label="Dienstleister"
											value={
												<Chip
													size="small"
													label={assessmentOrder.assignedTo.name}
												/>
											}
										/>
									</TextGroup>
									{assessmentOrder.assignedTo.name === "Dekra" && (
										<DekraAssessmentOrderTile order={assessmentOrder} />
									)}
									{assessmentOrder.assignedTo.name === "Macadam" && (
										<MacadamAssessmentOrderTile order={assessmentOrder} />
									)}
									{(assessmentOrder.isPoolfleet || assessmentOrder.doUnregistration) && (
										<TextGroup>
											<TextLine
												label="Zusatzinformationen"
												value={
													<>
														{assessmentOrder.isPoolfleet && (
															<Box>ALD Flex</Box>
														)}
														{assessmentOrder.doUnregistration && (
															<Box mt={2}>Abmeldung beauftragt</Box>
														)}
													</>
												}
											/>
										</TextGroup>
									)}
									<TextGroup title="Fahrzeug">
										<TextLine label="Fin" value={<VinDisplay vin={assessmentOrder.vin} />} />
										<TextLine label="Kennzeichen" value={assessmentOrder.plateNumber} />

										<TextLine label="Fahrzeugtyp" value={assessmentOrder.vehicle.type?.description} />
									</TextGroup>
								</TileContent>
							</Tile>
						</Grid>

						{vehicle && (
							<Grid item xs={12} md={6}>
								<VehicleAccidentsTile accidents={vehicle.accidents} />
							</Grid>
						)}

						<Grid item xs={12} md={6}>
							<Tile title="Historie">
								<TileContent>
									<TextGroup>
										<TextLine
											label="Erstellt"
											bold
											value={assessmentOrder.createdBy.name}
										/>
										<TextLine
											label=""
											value={moment(assessmentOrder.dateCreated).format(dateFormats.dateTime)}
										/>
										<TextLine
											label="Zuletzt angepasst"
											bold
											value={assessmentOrder.updatedBy.name}
										/>
										<TextLine
											label=""
											value={moment(assessmentOrder.dateUpdated).format(dateFormats.dateTime)}
										/>
									</TextGroup>
								</TileContent>
							</Tile>
						</Grid>
						{currentAssessment && (
							<Grid item xs={12} md={6}>
								<AssessmentOverviewTile
									assessment={currentAssessment}
								/>
							</Grid>
						)}

						{(assessmentOrder.defect || isEditingDefect) && (
							<Grid item xs={12} md={6}>
								<ExpertAssesmentTile
									title="Mängelmeldung"
									assesment={assessmentOrder}
									isLoading={isSubmitting}
									isEditingInterruption={isEditingDefect}
									setIsEditingInterruption={setIsEditingDefect}
									save={saveAssessmentInterruption}
								/>
							</Grid>
						)}

						{assessmentOrder.defect && isAldManager && (
							<Grid item xs={12} md={6}>
								<RepairOrderDisplay assessmentOrder={assessmentOrder} />
							</Grid>
						)}

						{(isEditingInquiryRequest || hasAnySubjectInqueries) &&
							<Grid item xs={12} md={6}>
								<InquiryAssesmentTile
									title="Sachverhaltsanfragen"
									assesment={assessmentOrder}
									setAssessmentOrder={setAssessmentOrder}
									isLoading={isSubmitting}
									setIsSubmitting={setIsSubmitting}
									hideSubjectInquiryForm={() => setIsEditingInquiryRequest(false)}
								/>
							</Grid>
						}
					</Grid>
				</Box>
			)}
		</Layout>
	);
};

export default AssessmentOrderDetails;
